import React, { useEffect } from 'react';
import { Button } from '@h2/ui';
import { useWorkSettingsStore } from '../../../stores/useWorkSettingsStore';
import { useTaskTypeStore } from '../../../stores/useTaskTypeStore';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';
import { AssignmentCell } from './AssignmentCell';

interface WorkerAssignmentStepProps {
  onNext?: () => void;
  onBack?: () => void;
  title?: string;
  description?: string;
  hideTitle?: boolean;
}

type TaskType = 'transcription' | 'translation' | 'transcription_review' | 'translation_review' | 'translation_review2';

export const WorkerAssignmentStep: React.FC<WorkerAssignmentStepProps> = ({
  title = "작업 설정 - 작업자 배정",
  description = "각 Task에 작업자 또는 작업그룹을 배정합니다.",
  hideTitle = false
}) => {
  // Configuration 스토어에서 데이터 가져오기
  const { formData, updateField } = useConfigurationStore();
  
  // 언어 설정 데이터
  const languageData = formData.languageSettingsComponent || { targetLanguages: [], tags: [], memo: '' };
  const targetLanguages = languageData.targetLanguages;
  
  // 워크플로 데이터
  const workflowData = formData.workflowComponent || { taskNodes: [], connections: [] };
  const taskNodes = workflowData.taskNodes;
  
  // 작업자 배정 데이터
  const workerData = formData.workerAssignmentComponent || { 
    assignmentMatrix: [], 
    availableWorkers: [], 
    availableGroups: [] 
  };

  // WorkSettings 스토어 (UI 상태 관리용)
  const { markStepCompleted } = useWorkSettingsStore();
  const { getTaskTypeById } = useTaskTypeStore();
  
  const languageNames: Record<string, string> = {
    'ko': '한국어',
    'en': '영어',
    'ja': '일본어',
    'zh': '중국어',
    'es': '스페인어',
    'hi': '힌디어'
  };
  
  // 작업자 배정 데이터 업데이트
  const updateAssignmentCell = (language: string, taskType: string, updates: any) => {
    console.log('작업자 배정 업데이트:', { language, taskType, updates });
    
    // 현재 assignmentMatrix 가져오기 또는 초기화
    const currentMatrix = workerData.assignmentMatrix || [];
    
    // 언어와 taskType에 해당하는 셀 찾기 또는 생성
    const cellKey = `${language}_${taskType}`;
    const existingIndex = currentMatrix.findIndex((item: any) => item.key === cellKey);
    
    const updatedCell = {
      key: cellKey,
      language,
      taskType,
      ...updates
    };
    
    let newMatrix;
    if (existingIndex >= 0) {
      // 기존 셀 업데이트
      newMatrix = [...currentMatrix];
      newMatrix[existingIndex] = { ...newMatrix[existingIndex], ...updatedCell };
    } else {
      // 새 셀 추가
      newMatrix = [...currentMatrix, updatedCell];
    }
    
    // Configuration Store에 업데이트
    updateField('workerAssignmentComponent', {
      ...workerData,
      assignmentMatrix: newMatrix
    });
  };

  useEffect(() => {
    // 임시로 더미 데이터 설정
    console.log('작업자 배정 컴포넌트 마운트됨');
    console.log('언어 목록:', targetLanguages);
    console.log('Task 노드:', taskNodes);
  }, [targetLanguages, taskNodes]);
  
  const validateAssignments = (): boolean => {
    // 임시로 항상 true 반환
    return true;
  };
  
  // BulkApplyButton 임시 구현
  const BulkApplyButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <Button variant="outline" size="sm" onClick={onClick}>
      {label}
    </Button>
  );
  
  const showBulkApplyDialog = (mode: string, target?: string) => {
    // TODO: 일괄 적용 다이얼로그 구현
    console.log('Bulk apply:', mode, target);
  };
  
  console.log('WorkerAssignmentStep 렌더링:', {
    targetLanguages: targetLanguages,
    taskNodes: taskNodes,
    languageData: languageData,
    workflowData: workflowData
  });

  if (targetLanguages.length === 0 || taskNodes.length === 0) {
    console.log('조건 실패:', { 
      targetLanguagesLength: targetLanguages.length, 
      taskNodesLength: taskNodes.length 
    });
    
    return (
      <div className="w-full max-w-none">
        {!hideTitle && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
        )}
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            이전 단계에서 언어와 워크플로를 먼저 설정해주세요.
          </p>
          <div className="mt-4 text-xs text-gray-400">
            디버그: 언어({targetLanguages.length}개), Task({taskNodes.length}개)
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full max-w-none">
      {!hideTitle && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
      )}
      <div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col className="w-24" />
              {taskNodes.map(taskNode => (
                <col key={taskNode.id} className="w-40" />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="border p-3 bg-muted text-left">
                  언어/Task
                </th>
                {taskNodes.map(taskNode => {
                  const taskTypeInfo = getTaskTypeById(taskNode.type);
                  const taskName = taskTypeInfo?.name || taskNode.type;
                  return (
                    <th key={taskNode.id} className="border p-3 bg-muted text-center">
                      <div className="min-h-[40px] flex flex-col justify-center">
                        <div className="font-medium">{taskName}</div>
                        {taskTypeInfo && !taskTypeInfo.languageDependent && (
                          <div className="text-xs text-muted-foreground font-normal">
                            (언어공통)
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {/* 언어종속 Task들 - 언어별로 행 생성 */}
              {targetLanguages.map((language, langIndex) => (
                <tr key={language} className={langIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border p-3 bg-muted font-medium">
                    {languageNames[language]}
                  </td>
                  {taskNodes.map((taskNode, taskIndex) => {
                    const taskTypeInfo = getTaskTypeById(taskNode.type);
                    const isLanguageDependent = taskTypeInfo?.languageDependent ?? true;
                    
                    if (!isLanguageDependent) {
                      // 언어종속이 아닌 경우, 첫 번째 언어에서만 표시
                      if (langIndex === 0) {
                        return (
                          <td key={taskNode.id} className="border p-2 align-top" rowSpan={targetLanguages.length}>
                            <AssignmentCell
                              cell={(() => {
                                const cellKey = `common_${taskNode.type}`;
                                const existingCell = workerData.assignmentMatrix?.find((item: any) => item.key === cellKey);
                                return existingCell || {
                                  languageCode: 'common',
                                  taskType: taskNode.type,
                                  assignee: { type: null, id: null, name: null },
                                  unitPrice: null,
                                  currency: 'KRW'
                                };
                              })()}
                              onUpdate={(updates) => 
                                updateAssignmentCell('common', taskNode.type, updates)
                              }
                            />
                          </td>
                        );
                      } else {
                        // 다른 언어에서는 셀을 생성하지 않음 (rowSpan으로 처리됨)
                        return null;
                      }
                    } else {
                      // 언어종속인 경우 정상적으로 표시
                      return (
                        <td key={taskNode.id} className="border p-2 align-top">
                          <AssignmentCell
                            cell={(() => {
                              const cellKey = `${language}_${taskNode.type}`;
                              const existingCell = workerData.assignmentMatrix?.find((item: any) => item.key === cellKey);
                              return existingCell || {
                                languageCode: language,
                                taskType: taskNode.type,
                                assignee: { type: null, id: null, name: null },
                                unitPrice: null,
                                currency: 'KRW'
                              };
                            })()}
                            onUpdate={(updates) => 
                              updateAssignmentCell(language, taskNode.type, updates)
                            }
                          />
                        </td>
                      );
                    }
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};